import React from 'react';
import {
  TextField,
  styled,
  Typography,
  Divider,
  Autocomplete,
} from '@mui/material';
import { Controller, Control } from 'react-hook-form';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import MultipleEntryInput from '../MultipleEntryInput';
import BasicSelect from 'further-ui/components/BasicSelect';
import timezones from 'timezones-list';
import { FormValues } from '../types';
import { isValidEmail } from 'further-ui/utils';

const timezoneOptions = timezones.map((timezone) => ({
  value: timezone.tzCode,
  label: timezone.name,
}));

const StyledColumn = styled(Column)({
  width: '100%',
  maxWidth: '600px',
});

const MaxWidthTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(theme.spacingSizes.lg),
  maxWidth: '400px',
}));

type Props = {
  control: Control<FormValues>;
  hasLinkedPortal: boolean;
  sharedFieldWarningText: string;
  sharedFieldDotColor: string;
};

const General: React.FC<Props> = ({
  control,
  hasLinkedPortal,
  sharedFieldWarningText,
  sharedFieldDotColor,
}) => {
  const dotColor = hasLinkedPortal ? sharedFieldDotColor : undefined;

  return (
    <StyledColumn spacing="lg">
      {hasLinkedPortal ? (
        <Typography variant="body2" color="grey.500">
          {sharedFieldWarningText}
        </Typography>
      ) : null}

      <FormField
        label="Firm name"
        size="md"
        dotColor={dotColor}
        dotTooltipText={
          hasLinkedPortal
            ? 'This field is shared between your EIS and LP systems. Changes here will affect both systems.'
            : undefined
        }
      >
        <Controller
          name="firmName"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Enter firm name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>

      <Divider />

      <FormField label="Key contacts">
        <Controller
          name="keyContactEmails"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <MultipleEntryInput
              inputPlaceholder="Email address"
              buttonText="Add"
              inputMaxWidth={400}
              value={value ?? []}
              duplicateValueErrorMessage="This contact has already been chosen."
              onChange={onChange}
              validate={isValidEmail}
              validationErrorMessage="Please enter a valid email address."
              formErrorMessages={
                error as unknown as Array<{ message?: string }>
              }
            />
          )}
        />
      </FormField>

      <FormField label="System date format" size="md">
        <Controller
          name="systemDateFormat"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <BasicSelect
              options={[{ label: 'MMM DD, YYYY', value: 'MMM DD, YYYY' }]}
              value={field.value}
              onChange={field.onChange}
              error={!!error}
              helperText={error?.message}
              placeholder="Select a date format"
              fullWidth
            />
          )}
        />
      </FormField>

      <FormField label="Time zone" size="md">
        <Controller
          name="timezone"
          control={control}
          render={({ fieldState: { error }, field: { value, onChange } }) => (
            <Autocomplete
              fullWidth
              disableClearable
              options={timezoneOptions}
              placeholder="Select timezone"
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.value}
              value={timezoneOptions.find(
                (timezone) => timezone.value === value,
              )}
              onChange={(_, newValue) => {
                onChange(newValue?.value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  variant="outlined"
                  error={!!error}
                  helperText={error?.message}
                  placeholder="Select timezone"
                />
              )}
            />
          )}
        />
      </FormField>

      <FormField label="Subscription flow email recipient(s)">
        <MaxWidthTypography variant="body2" color="grey.600">
          Enter the email addresses you wish to be blind-copied into client
          subscription flow emails. These include registration and notification
          emails. Due to the large volume of potential emails, we recommend
          personal email addresses are not used.
        </MaxWidthTypography>
        <Controller
          name="subscriptionFlowEmailRecipients"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <MultipleEntryInput
              inputPlaceholder="Email address"
              buttonText="Add"
              inputMaxWidth={400}
              value={value ?? []}
              duplicateValueErrorMessage="This contact has already been chosen."
              onChange={onChange}
              validate={isValidEmail}
              validationErrorMessage="Please enter a valid email address."
              formErrorMessages={
                error as unknown as Array<{ message?: string }>
              }
            />
          )}
        />
      </FormField>
    </StyledColumn>
  );
};

export default General;
